"use client";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import {
  Download,
  LogOut,
  Monitor,
  Moon,
  Palette,
  Settings,
  ShieldCheck,
  Sun,
} from "lucide-react";
import Link from "next/link";
import { useTheme } from "next-themes";
import { getConfigurationPath } from "@/lib/constants/permissions";
import { logout } from "@/features/users/logout.actions";
import {
  getDesktopBridge,
  useIsDesktopApp,
  useIsWindowsBrowser,
} from "@/lib/desktop/bridge";
import { DESKTOP_APP_DOWNLOAD_PATH } from "@/lib/desktop/download";
import { resetUser } from "@/lib/posthog-identify";

export function NavUser({
  user,
  orgSlug,
  userPermissions,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
  orgSlug: string;
  userPermissions: string[];
}) {
  const configurationPath = getConfigurationPath(userPermissions);
  const { theme, setTheme } = useTheme();
  const isDesktopApp = useIsDesktopApp();
  const isWindowsBrowser = useIsWindowsBrowser();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button className="rounded-full" size="icon" variant="ghost" />}
      >
        <Avatar className="h-8 w-8 rounded-full">
          <AvatarImage alt={user.name} src={user.avatar} />
          <AvatarFallback className="rounded-full">
            {user.name.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-(--anchor-width) min-w-56 rounded-lg"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage alt={user.name} src={user.avatar} />
                <AvatarFallback className="rounded-lg">
                  {user.name.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
            </div>
          </DropdownMenuLabel>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {configurationPath && (
            <DropdownMenuItem
              className="lg:hidden"
              render={<Link href={`/${orgSlug}${configurationPath}`} />}
            >
              <Settings className="text-muted-foreground" />
              Configuración
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            render={<Link href={`/${orgSlug}/cuenta/seguridad`} />}
          >
            <ShieldCheck className="text-muted-foreground" />
            Seguridad de la cuenta
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Palette className="text-muted-foreground" />
              Tema
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="space-y-1">
              <DropdownMenuItem
                className={theme === "light" ? "bg-accent" : ""}
                onClick={() => setTheme("light")}
              >
                <Sun />
                Claro
              </DropdownMenuItem>
              <DropdownMenuItem
                className={theme === "dark" ? "bg-accent" : ""}
                onClick={() => setTheme("dark")}
              >
                <Moon />
                Oscuro
              </DropdownMenuItem>
              <DropdownMenuItem
                className={theme === "system" ? "bg-accent" : ""}
                onClick={() => setTheme("system")}
              >
                <Monitor />
                Sistema
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          {isDesktopApp && getDesktopBridge()?.openRegister ? (
            <DropdownMenuItem
              onClick={() => void getDesktopBridge()?.openRegister?.()}
            >
              <Monitor className="text-muted-foreground" />
              Usar esta PC como caja
            </DropdownMenuItem>
          ) : null}
          {isWindowsBrowser && (
            <DropdownMenuItem render={<a href={DESKTOP_APP_DOWNLOAD_PATH} />}>
              <Download className="text-muted-foreground" />
              Descargar app
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => {
            resetUser();
            await logout();
          }}
        >
          <LogOut />
          Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
